import React, { useState } from 'react';
import styled from '@emotion/styled';
import { SeminarSummary } from '@/pages/Seminar/types';
import {
    CAREER_COLOR,
    STATUS_BADGE_COLOR,
    buildPlaceLabel,
    buildScheduleLabel,
    formatFee,
    isCareerSeminar,
    isOpenSchedule,
    isRecruitmentOver,
    remainingSeatsOf,
    statusBadgeOf,
} from '@/components/domain/seminar/seminarDisplay';
import { UI_KIND, uiTargetAttrs } from '@/common/uiEventNames';

/*
 * 세미나 목록 카드 (seminar-format-spec.md 3절, 2026-09-28 사용자 피드백: 빈 줄로 공간 낭비 금지).
 *   썸네일 16:9 → (배지: 오른쪽 위 상태 하나만, 작게)
 *   → 워드마크·남은 자리 → 제목(최대 2줄, 빈 줄로 채우지 않음) → 일정 → 장소·강사 → 가격·별점
 * 같은 행의 카드 높이는 그리드 stretch 로 맞춘다. 커리어는 가격·별점 줄을 아래에 맞춘다.
 * 좁은 카드(글 칸 폭 190px 이하 — 3단 홈의 2장 나란히, 폰 2열 목록)는 가격 줄 구성을 고정한다(2026-10-01 사용자 지적 "왜 통일이 안 돼"):
 *   1줄 = 할인율 · 가격, 2줄 = 취소선 정가(왼쪽) · 별점(오른쪽). 금액 자릿수·할인율 자릿수에 따라 어떤 카드는 한 줄, 어떤 카드는 두 줄이 되지 않게.
 * layout="horizontal": 한 장뿐일 때 전체 폭 카드.
 *   모바일: 세로형 대표 카드 — 16:9 썸네일을 카드 폭 가득 위에, 아래 본문(2026-09-29 사용자: 왼쪽 작은 썸네일 배치가 어색함).
 *   태블릿·PC(>768): 가로형 — 목록 폭 전체, 썸네일은 카드 왼쪽 가장자리에 여백 없이 16:9 약 40%,
 *   오른쪽 본문은 세로 가운데(워드마크·제목 18px·일정·장소·가격, 바닥 줄은 본문 바로 아래). 2026-09-28 사용자 피드백.
 */

const MOBILE_MAX_WIDTH_PX: number = 768;
const WIDE: string = `@media (min-width: ${MOBILE_MAX_WIDTH_PX + 1}px)`;
/** 좁은 카드 — 글 칸(Body) 안쪽 폭이 이 값 이하면 "할인율 가격 정가"가 한 줄에 안정적으로 들어가지 않는다 */
const NARROW: string = '@container seminar-card-body (max-width: 190px)';

const Card = styled('div', { shouldForwardProp: (prop: string) => prop !== 'horizontal' && prop !== 'fullWidthMobile' })<{ horizontal?: boolean; fullWidthMobile?: boolean }>`
  /* 폰의 1열 커리어 카드는 같은 정보 구성을 유지하되 넓은 본문에 맞춰 읽기 쉽게 표시한다. */
  ${({ fullWidthMobile }) => fullWidthMobile ? `
    width: 100%;
    @media (max-width: ${MOBILE_MAX_WIDTH_PX}px) {
      --seminar-title-size: 20px;
      --seminar-price-size: 20px;
      --seminar-rate-size: 15px;
      --seminar-badge-size: 11px;
      --seminar-meta-size: 14px;
      --seminar-meta-height: 20px;
      --seminar-wordmark-size: 12px;
      --seminar-wordmark-height: 17px;
      --seminar-aux-size: 12px;
      --seminar-aux-height: 18px;
      --seminar-rating-size: 14px;
      --seminar-price-wrap: nowrap;
      --seminar-amount-flex: 1;
      --seminar-body-gap: 6px;
      --seminar-body-padding: 14px 16px 16px;
    }
  ` : ''}
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  box-sizing: border-box;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${CAREER_COLOR};
    outline-offset: 2px;
  }

  ${({ horizontal }) => (horizontal ? `
    width: 100%;
    height: auto;

    /* 모바일은 세로형 그대로(썸네일 위·폭 가득). 태블릿·PC 만 가로형 — 썸네일을 카드 왼쪽 가장자리에 여백 없이(16:9, 약 40%) */
    ${WIDE} {
      flex-direction: row;

      > :first-of-type {
        width: 40%;
      }
    }
  ` : '')}
`;

const Thumb = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #eef1f0;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
`;

const ThumbImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
`;

/**
 * 오른쪽 위 상태 배지 하나만(모집마감·마감임박·판매 종료·폐강) — 작게(높이 18px, 글자 10~11px), 이미지를 가리지 않게.
 * 진행 방식은 본문 "동영상 · 강사명", 쿠폰은 가격 줄 할인율과 겹쳐 배지로 두지 않는다.
 */
const StatusBadge = styled('span', { shouldForwardProp: (prop: string) => prop !== 'textColor' })<{ textColor: string }>`
  position: absolute;
  z-index: 1;
  right: 8px;
  top: 8px;
  height: 18px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  font-size: var(--seminar-badge-size, 10px);
  font-weight: 800;
  white-space: nowrap;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.9);
  color: ${({ textColor }) => textColor};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  @media (min-width: ${MOBILE_MAX_WIDTH_PX + 1}px) {
    font-size: var(--seminar-badge-size, 11px);
  }
`;

const Body = styled('div', { shouldForwardProp: (prop: string) => prop !== 'horizontal' })<{ horizontal?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--seminar-body-gap, 4px);
  padding: var(--seminar-body-padding, 10px 12px 12px);
  min-width: 0;
  /* 가격 줄이 카드 폭(글 칸 폭)에 따라 줄 구성을 바꾼다 — NARROW */
  container-type: inline-size;
  container-name: seminar-card-body;

  ${({ horizontal }) => (horizontal ? `
    ${WIDE} {
      justify-content: center;
      gap: 6px;
      padding: 20px 28px;
    }
  ` : '')}
`;

const Wordmark = styled.div<{ career: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  height: var(--seminar-wordmark-height, 15px);
  font-size: var(--seminar-wordmark-size, 11px);
  line-height: var(--seminar-wordmark-height, 15px);
  font-weight: 800;
  color: ${({ career }) => (career ? CAREER_COLOR : '#9ca3af')};
  white-space: nowrap;
  overflow: hidden;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ career }) => (career ? CAREER_COLOR : '#d1d5db')};
  }
`;

const Title = styled('div', { shouldForwardProp: (prop: string) => prop !== 'horizontal' && prop !== 'career' })<{ horizontal?: boolean; career: boolean }>`
  font-size: var(--seminar-title-size, ${({ horizontal, career }) => horizontal ? 16 : career ? 17 : 14}px);
  font-weight: 700;
  line-height: 1.35;
  color: #111827;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: keep-all;
  overflow-wrap: anywhere;

  @media (min-width: ${MOBILE_MAX_WIDTH_PX + 1}px) {
    font-size: var(--seminar-title-size, ${({ horizontal, career }) => horizontal ? 18 : career ? 17 : 15}px);
  }
`;

/** 커리어는 워드마크 오른쪽에 남은 자리, 일반 가로형만 분야·대상 칩. */
const TopLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-end;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
`;

const Tag = styled.span<{ level?: boolean }>`
  flex-shrink: 0;
  height: 22px;
  line-height: 22px;
  padding: 0 8px;
  border-radius: 11px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  background: ${({ level }) => (level ? '#eef2ff' : '#f1f5f4')};
  color: ${({ level }) => (level ? '#3730a3' : '#4f5b58')};
`;

const MAX_TAG_CATEGORIES: number = 2;

const MetaLine = styled.div`
  height: var(--seminar-meta-height, 17px);
  font-size: var(--seminar-meta-size, 12px);
  line-height: var(--seminar-meta-height, 17px);
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * 가격 한 줄 — "9% 118,300원 130,000원(취소선)". 할인 없으면 가격만.
 * 좁은 2열 카드에서 한 줄에 다 안 들어가면 취소선 정가만 다음 줄로 내린다(말줄임으로 자르지 않는다).
 */
const PriceRow = styled.div`
  display: flex;
  flex: var(--seminar-amount-flex, initial);
  min-width: 0;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 5px;
  margin-top: 2px;
  line-height: 22px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;

  /* 좁은 카드: 줄 나눔은 바깥 줄(PriceLine)이 맡는다 — 할인율·가격·정가가 바깥 줄의 항목이 된다 */
  ${NARROW} {
    display: contents;
  }
`;

const PriceLine = styled('div', { shouldForwardProp: (prop: string) => prop !== 'career' && prop !== 'horizontal' })<{ career: boolean; horizontal: boolean }>`
  display: flex;
  flex-wrap: var(--seminar-price-wrap, wrap);
  align-items: baseline;
  justify-content: space-between;
  column-gap: 8px;
  row-gap: 4px;
  /* 커리어 세로형은 가격 줄을 카드 바닥에 붙인다(같은 줄 카드끼리 가격 높이가 맞는다).
     --seminar-price-push: 0 을 주면 장소 줄 바로 아래에 붙고 남는 높이는 카드 아래로 간다(PC 홈 오늘의 세미나) */
  margin-top: ${({ career, horizontal }) => career && !horizontal ? 'var(--seminar-price-push, auto)' : '0'};

  /* 좁은 카드: 1줄 = 할인율 · 가격, 2줄 = 취소선 정가(왼쪽) · 별점(오른쪽). 가운데 줄바꿈 항목으로 강제로 나눈다 */
  ${NARROW} {
    flex-wrap: wrap;
    justify-content: flex-start;
    column-gap: 5px;
    row-gap: 0;
    padding-top: 2px;
    line-height: 22px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &::before {
      content: '';
      order: 1;
      flex-basis: 100%;
      height: 0;
    }
  }
`;

const RatingLine = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: var(--seminar-rating-size, 11px);
  line-height: 18px;
  white-space: nowrap;

  ${NARROW} {
    order: 2;
  }
`;

/** 할인율 — 빨강·굵게 */
const RateText = styled.span`
  font-size: var(--seminar-rate-size, 13px);
  font-weight: 800;
  color: #e11d48;

  @media (min-width: ${MOBILE_MAX_WIDTH_PX + 1}px) {
    font-size: var(--seminar-rate-size, 14px);
  }
`;

/** 취소선 정가 — 가격 오른쪽 작은 회색 */
const WasPrice = styled.span`
  font-size: 11px;
  line-height: 16px;
  color: #9ca3af;
  text-decoration: line-through;

  ${NARROW} {
    order: 2;
    line-height: 18px;
  }
`;

const NowPrice = styled.span<{ career: boolean }>`
  font-size: var(--seminar-price-size, 15px);

  @media (min-width: ${MOBILE_MAX_WIDTH_PX + 1}px) {
    font-size: var(--seminar-price-size, 16px);
  }

  font-weight: ${({ career }) => (career ? 800 : 400)};
  color: ${({ career }) => (career ? '#111827' : '#374151')};
`;

const Rating = styled.span`
  color: #b45309;
  font-weight: 700;
`;

const Muted = styled.span`
  color: #9ca3af;
`;

const Seats = styled.span`
  margin-left: auto;
  font-size: var(--seminar-aux-size, 11px);
  line-height: var(--seminar-aux-height, 15px);
  white-space: nowrap;
  color: #dc2626;
  font-weight: 700;
`;

/**
 * 카드가 그리는 데 필요한 값 — 목록 요약(SeminarSummary)과 홈 요약(todaySeminars)이 모두 맞도록 id·제목만 필수.
 * 없는 값은 칸을 비워 둔 채 높이만 유지한다.
 */
export type SeminarCardData = Partial<Omit<SeminarSummary, 'id' | 'title'>> & {
  id: string | number;
  title: string;
};

interface SeminarCardProps {
  seminar: SeminarCardData;
  onClick: (id: string | number) => void;
  /** 강사 화면처럼 강사명이 뻔한 곳에서는 장소만 */
  hideInstructor?: boolean;
  /** 한 장뿐일 때 전체 폭 가로형 */
  layout?: 'vertical' | 'horizontal';
  /** 모바일 커리어 목록의 1열 카드에 맞춘 본문 크기·간격 */
  fullWidthMobile?: boolean;
  /** 미리보기 전용(등록 폼) — 참가비가 아직 없으면(participationFee·price 둘 다 없음) '무료' 대신 이 문구. 목록 카드는 넘기지 않는다 */
  emptyPriceText?: string;
}

const SeminarCard: React.FC<SeminarCardProps> = ({ seminar, onClick, hideInstructor = false, layout = 'vertical', fullWidthMobile = false, emptyPriceText }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const horizontal: boolean = layout === 'horizontal';

  // 미지정/그 외는 일반(GENERAL)로 본다 (레거시 안전)
  const isCareer: boolean = isCareerSeminar(seminar);

  // 썸네일 1장 통일 — 모바일도 원본 썸네일만 쓴다(mobileThumbnailUrl 은 더 이상 보지 않는다)
  const displayThumbnail: string | undefined = seminar.thumbnailUrl;

  const statusBadge = statusBadgeOf(seminar);
  // 분야 이름(중복 제거)과 대상 — 대상 "무관"은 보이지 않는다
  const categoryNames: string[] = Array.from(new Set((seminar.categories ?? []).map((c) => c?.displayName).filter((n): n is string => Boolean(n))));
  const rawLevel = seminar.level as { code?: string; displayName?: string } | string | undefined;
  const levelCode: string | undefined = typeof rawLevel === 'string' ? rawLevel : rawLevel?.code;
  const levelName: string | null = typeof rawLevel === 'object' && rawLevel?.displayName && levelCode !== 'ANY' ? rawLevel.displayName : null;
  const scheduleLabel: string = buildScheduleLabel(seminar);
  const placeLabel: string = buildPlaceLabel(seminar);
  const instructorName: string = seminar.instructorNickname || seminar.instructor || '';
  const placeLine: string = hideInstructor
    ? placeLabel
    : [placeLabel, instructorName].filter(Boolean).join(' · ');

  const remaining: number | null = remainingSeatsOf(seminar);
  const showSeats: boolean = isCareer && remaining !== null && remaining > 0 && !isRecruitmentOver(seminar);
  // 별점은 세미나가 아니라 강사 누적(2026-09-28 결정) — 세미나는 일회성이라 후기가 쌓이지 않는다. 서버가 아직 안 주면 세미나 값으로 폴백
  // 별점·후기 수는 한 쌍으로 고른다(강사 값과 세미나 값이 섞이면 "★ 강사 별점 (세미나 후기 수)"가 된다)
  const useInstructorRating: boolean = typeof seminar.instructorReviewCount === 'number';
  const ratingValue: number = (useInstructorRating ? seminar.instructorRating : seminar.averageRating) ?? 0;
  const reviewCount: number = (useInstructorRating ? seminar.instructorReviewCount : seminar.reviewCount) ?? 0;

  // 쿠폰 적용가 — 목록 응답의 최대 할인(bestCouponDiscount)이 있을 때만. 없으면 정가만(취소선 줄은 빈 줄)
  const listPrice: number = seminar.participationFee ?? seminar.price ?? 0;
  // 모집 마감·시작·종료된 세미나는 쿠폰을 쓸 수 없으니 정가만(서버도 null 로 주지만 화면에서도 막는다)
  // 상시 동영상은 시작(=판매 시작) 뒤에도 파니 시작 조건을 빼고 판매 종료로만 막는다
  const startedAt: number = seminar.startDateTime ? new Date(seminar.startDateTime).getTime() : NaN;
  const openSale: boolean = isOpenSchedule(seminar);
  const couponClosed: boolean = isRecruitmentOver(seminar)
    || seminar.salesClosed === true
    || (!openSale && !Number.isNaN(startedAt) && startedAt <= Date.now())
    || (seminar.status !== undefined && seminar.status !== 'PUBLISHED');
  const bestDiscount: number = isCareer && listPrice > 0 && !couponClosed ? Math.max(0, seminar.bestCouponDiscount ?? 0) : 0;
  const hasCoupon: boolean = bestDiscount > 0 && bestDiscount < listPrice;
  const couponRate: number = hasCoupon
    ? (seminar.bestCouponRate ?? Math.floor((bestDiscount / listPrice) * 100))
    : 0;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(seminar.id);
    }
  };

  return (
    <Card role="link" tabIndex={0} horizontal={horizontal} fullWidthMobile={fullWidthMobile} {...uiTargetAttrs(UI_KIND.SEMINAR, seminar.id)} onClick={() => onClick(seminar.id)} onKeyDown={handleKeyDown}>
      <Thumb>
        {displayThumbnail && !imageFailed ? (
          <ThumbImage src={displayThumbnail} alt="" loading="lazy" onError={() => setImageFailed(true)} />
        ) : (
          <span>이미지 없음</span>
        )}
        {statusBadge && <StatusBadge textColor={STATUS_BADGE_COLOR[statusBadge.tone]}>{statusBadge.text}</StatusBadge>}
      </Thumb>

      <Body horizontal={horizontal}>
        {isCareer ? (
          <TopLine>
            <Wordmark career>명치사 커리어</Wordmark>
            {showSeats && <Seats>{remaining}자리 남음</Seats>}
          </TopLine>
        ) : horizontal && (categoryNames.length > 0 || levelName) ? (
          <TopLine>
            <Wordmark career={isCareer}>{isCareer ? '명치사 커리어' : '일반 세미나'}</Wordmark>
            <TagRow aria-label="분야와 대상">
              {categoryNames.slice(0, MAX_TAG_CATEGORIES).map((name: string) => <Tag key={name}>{name}</Tag>)}
              {categoryNames.length > MAX_TAG_CATEGORIES && <Tag>+{categoryNames.length - MAX_TAG_CATEGORIES}</Tag>}
              {levelName && <Tag level>{levelName}</Tag>}
            </TagRow>
          </TopLine>
        ) : (
          <Wordmark career={isCareer}>{isCareer ? '명치사 커리어' : '일반 세미나'}</Wordmark>
        )}
        <Title horizontal={horizontal} career={isCareer}>{seminar.title}</Title>
        <MetaLine>{scheduleLabel}</MetaLine>
        <MetaLine>{placeLine}</MetaLine>

        <PriceLine career={isCareer} horizontal={horizontal}>
          <PriceRow>
            {hasCoupon && couponRate > 0 && <RateText>{couponRate}%</RateText>}
            <NowPrice career={isCareer}>
              {emptyPriceText && seminar.participationFee == null && seminar.price == null ? emptyPriceText : formatFee(listPrice - bestDiscount)}
            </NowPrice>
            {hasCoupon && <WasPrice aria-label={`정가 ${formatFee(listPrice)}`}>{formatFee(listPrice)}</WasPrice>}
          </PriceRow>
          {isCareer && (
            <RatingLine>
              {reviewCount > 0 ? (
                <Rating title="강사 누적 별점">★ {ratingValue.toFixed(1)} ({reviewCount})</Rating>
              ) : (
                <Muted>후기 없음</Muted>
              )}
            </RatingLine>
          )}
        </PriceLine>
      </Body>
    </Card>
  );
};

export default SeminarCard;
